import { useState, useCallback } from 'react'
import type { CalculateLayoutInput, LayoutCalculationResult } from '../../types/infrastructure-map'
import { calculateLayout, calculateAndApplyLayout } from '../../services/infrastructure-map'
import { logger } from '../../utils/logger'

export interface UseLayoutCalculationResult {
  calculate: (input: CalculateLayoutInput) => Promise<LayoutCalculationResult | null>
  calculateAndApply: (input: CalculateLayoutInput) => Promise<{
    layout: LayoutCalculationResult
    updated: number
  } | null>
  isCalculating: boolean
  lastResult: LayoutCalculationResult | null
  error: string | null
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function useLayoutCalculation(caseId: string | null): UseLayoutCalculationResult {
  const [isCalculating, setIsCalculating] = useState(false)
  const [lastResult, setLastResult] = useState<LayoutCalculationResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const calculate = useCallback(
    async (input: CalculateLayoutInput): Promise<LayoutCalculationResult | null> => {
      if (!caseId) return null
      setIsCalculating(true)
      setError(null)
      try {
        const result = await calculateLayout(caseId, input)
        setLastResult(result)
        return result
      } catch (err) {
        logger.warn('useLayoutCalculation', 'Failed to calculate layout', err)
        setError(errorMessage(err, 'Failed to calculate layout'))
        return null
      } finally {
        setIsCalculating(false)
      }
    },
    [caseId]
  )

  const calculateAndApply = useCallback(
    async (
      input: CalculateLayoutInput
    ): Promise<{ layout: LayoutCalculationResult; updated: number } | null> => {
      if (!caseId) return null
      setIsCalculating(true)
      setError(null)
      try {
        const result = await calculateAndApplyLayout(caseId, input)
        setLastResult(result.layout)
        return result
      } catch (err) {
        logger.warn('useLayoutCalculation', 'Failed to calculate and apply layout', err)
        setError(errorMessage(err, 'Failed to calculate and apply layout'))
        return null
      } finally {
        setIsCalculating(false)
      }
    },
    [caseId]
  )

  return { calculate, calculateAndApply, isCalculating, lastResult, error }
}
